import { authenticatedInstance } from '../index/index.service';
import { defaultStreamUrl, subscribeStream } from '../sse-subscribe/sse-subscribe.util';
import type { KitchenStatusValue, KdsOrderView, KdsOrderlineView } from './kds.service';

// Summary: KDS SSE event union used by KdsPage reducers.
// Details: docs/function-library/web/services.md#kds-service
export type KdsStreamEvent =
  | { type: 'kds.order.opened'; order: KdsOrderView }
  | { type: 'kds.order.closed'; orderId: number }
  | { type: 'kds.session.closed'; sessionId: number }
  | { type: 'kds.order.vip_changed'; orderId: number; isVip: boolean }
  | { type: 'kds.orderline.added'; orderId: number; orderline: KdsOrderlineView }
  | { type: 'kds.orderline.status_changed'; orderlineId: number; orderId: number; kitchenStatus: KitchenStatusValue | null; cancelled?: boolean; wasPulledBack?: boolean; updatedAt?: string }
  | { type: 'kds.orderline.note_changed'; orderlineId: number; orderId: number; note: string | null }
  | { type: 'kds.selection.status_changed'; selectionId: number; orderlineId: number; orderId: number; kitchenStatus: KitchenStatusValue | null; wasPulledBack?: boolean }
  | { type: 'tenant.maintenance-mode-changed'; maintenanceMode: boolean }
  | { type: 'kds.refresh-requested' };

// Summary: Shared object-type guard used by runtime event parsing.
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// Summary: Validates kitchen status values accepted by stream deltas.
function isKitchenStatusValue(value: unknown): value is KitchenStatusValue | null {
  return value === null || value === 'COOKING' || value === 'READY' || value === 'DONE' || value === 'HIDE';
}

// Summary: Minimal structural guard for kds.orderline.added payload.
function isKdsOrderlineView(value: unknown): value is KdsOrderlineView {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === 'number' &&
    typeof value.orderId === 'number' &&
    typeof value.productName === 'string' &&
    typeof value.quantity === 'number'
  );
}

// Summary: Minimal structural guard for kds.order.opened payload.
function isKdsOrderView(value: unknown): value is KdsOrderView {
  if (!isRecord(value)) return false;
  return typeof value.id === 'number' && Array.isArray(value.orderlines);
}

// Summary: Runtime parser that validates and narrows raw SSE payloads.
function parseKdsStreamEvent(payload: unknown): KdsStreamEvent | null {
  if (!isRecord(payload) || typeof payload.type !== 'string') {
    return null;
  }

  switch (payload.type) {
    case 'kds.order.opened':
      return isKdsOrderView(payload.order)
        ? { type: 'kds.order.opened', order: payload.order }
        : null;
    case 'kds.order.closed':
      return typeof payload.orderId === 'number'
        ? { type: 'kds.order.closed', orderId: payload.orderId }
        : null;
    case 'kds.session.closed':
      return typeof payload.sessionId === 'number'
        ? { type: 'kds.session.closed', sessionId: payload.sessionId }
        : null;
    case 'kds.order.vip_changed':
      return typeof payload.orderId === 'number' && typeof payload.isVip === 'boolean'
        ? { type: 'kds.order.vip_changed', orderId: payload.orderId, isVip: payload.isVip }
        : null;
    case 'kds.orderline.added':
      return typeof payload.orderId === 'number' && isKdsOrderlineView(payload.orderline)
        ? { type: 'kds.orderline.added', orderId: payload.orderId, orderline: payload.orderline }
        : null;
    case 'kds.orderline.status_changed':
      return (
        typeof payload.orderlineId === 'number' &&
        typeof payload.orderId === 'number' &&
        isKitchenStatusValue(payload.kitchenStatus)
      )
        ? {
            type: 'kds.orderline.status_changed',
            orderlineId: payload.orderlineId,
            orderId: payload.orderId,
            kitchenStatus: payload.kitchenStatus,
            cancelled: typeof payload.cancelled === 'boolean' ? payload.cancelled : undefined,
            wasPulledBack: typeof payload.wasPulledBack === 'boolean' ? payload.wasPulledBack : undefined,
            updatedAt: typeof payload.updatedAt === 'string' ? payload.updatedAt : undefined,
          }
        : null;
    case 'kds.selection.status_changed':
      return (
        typeof payload.selectionId === 'number' &&
        typeof payload.orderlineId === 'number' &&
        typeof payload.orderId === 'number' &&
        isKitchenStatusValue(payload.kitchenStatus)
      )
        ? {
            type: 'kds.selection.status_changed',
            selectionId: payload.selectionId,
            orderlineId: payload.orderlineId,
            orderId: payload.orderId,
            kitchenStatus: payload.kitchenStatus,
            wasPulledBack: typeof payload.wasPulledBack === 'boolean' ? payload.wasPulledBack : undefined,
          }
        : null;
    case 'kds.orderline.note_changed':
      return (
        typeof payload.orderlineId === 'number' &&
        typeof payload.orderId === 'number' &&
        (payload.note === null || typeof payload.note === 'string')
      )
        ? {
            type: 'kds.orderline.note_changed',
            orderlineId: payload.orderlineId,
            orderId: payload.orderId,
            note: payload.note as string | null,
          }
        : null;
    case 'tenant.maintenance-mode-changed':
      return typeof payload.maintenanceMode === 'boolean'
        ? { type: 'tenant.maintenance-mode-changed', maintenanceMode: payload.maintenanceMode }
        : null;
    case 'kds.refresh-requested':
      return { type: 'kds.refresh-requested' };
    default:
      return null;
  }
}

// Summary: Mints a short-lived KDS stream handshake ticket (employee session, or JWT-only viewer).
async function mintKdsStreamTicket(tenantId: string, employeeSession?: string): Promise<string> {
  const response = await authenticatedInstance.post<{ ticket: string; expiresAt: number }>(
    `/kds/tenant/${encodeURIComponent(tenantId)}/stream-ticket`,
    {},
    { headers: employeeSession ? { 'x-employee-session': employeeSession } : undefined },
  );
  if (!response.data?.ticket) {
    throw new Error('KDS stream ticket missing in response');
  }
  return response.data.ticket;
}

// Summary: Opens the tenant KDS EventSource stream with ticket-based reconnect handling.
export function subscribeKdsStream(
  tenantId: string,
  employeeSession: string | undefined,
  onEvent: (event: KdsStreamEvent) => void,
  onError?: () => void,
  onResync?: () => void,
): () => void {
  return subscribeStream<KdsStreamEvent>({
    mintTicket: () => mintKdsStreamTicket(tenantId, employeeSession),
    buildStreamUrl: (ticket, lastEventId) =>
      defaultStreamUrl(`/kds/tenant/${encodeURIComponent(tenantId)}/stream`, ticket, lastEventId),
    parseEvent: parseKdsStreamEvent,
    onEvent,
    onError,
    onResync,
  });
}
